import React, { useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, Save, Trash2 } from "lucide-react";

/** Formulaire des clés Stripe plateforme (secrète chiffrée, publiable, secret webhook). */
export function StripeSettingsForm({ cfg, onSaved }) {
  const [form, setForm] = useState({ secret_key: "", publishable_key: cfg.publishable_key || "", webhook_secret: "" });
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const body = { publishable_key: form.publishable_key.trim() };
      if (form.secret_key.trim()) body.secret_key = form.secret_key.trim();
      if (form.webhook_secret.trim()) body.webhook_secret = form.webhook_secret.trim();
      const { data } = await api.put("/admin/stripe/settings", body);
      toast.success(data.verified_account ? `Clé vérifiée — ${data.verified_account.business_name || data.verified_account.account_id} (${data.mode}) · chiffrée en base` : "Configuration Stripe enregistrée");
      setForm({ ...form, secret_key: "", webhook_secret: "" });
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const removeKey = async () => {
    if (!window.confirm("Retirer la clé enregistrée ? Stripe sera désactivé et la configuration serveur reprendra la main.")) return;
    try { await api.delete("/admin/stripe/settings/secret"); toast.success("Clé retirée — Stripe désactivé"); onSaved?.(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-white border border-[#DEE2E6] p-5 space-y-4" data-testid="stripe-form">
      <div className="text-xs uppercase tracking-[0.2em] font-semibold flex items-center gap-2"><KeyRound className="h-4 w-4" />Clés API Stripe</div>
      <p className="text-xs text-[#495057]">Dashboard Stripe → Développeurs → Clés API. La clé secrète est vérifiée auprès de Stripe puis <b>chiffrée</b> avant enregistrement ; elle n'est jamais réaffichée.</p>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Clé secrète (sk_live_… / sk_test_…)</Label>
        <div className="flex gap-2 mt-1">
          <Input type={show ? "text" : "password"} value={form.secret_key} onChange={(e) => setForm({ ...form, secret_key: e.target.value })} placeholder={cfg.configured ? `Actuelle : ${cfg.secret_key_masked} — saisir pour remplacer` : "sk_live_…"} data-testid="stripe-secret-key" className="h-10 font-mono" />
          <Button type="button" variant="outline" onClick={() => setShow((s) => !s)} data-testid="stripe-toggle-secret" className="h-10 w-10 p-0">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
        </div>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Clé publiable (pk_…) — facultative</Label>
        <Input value={form.publishable_key} onChange={(e) => setForm({ ...form, publishable_key: e.target.value })} placeholder="pk_live_…" data-testid="stripe-publishable-key" className="h-10 mt-1 font-mono" />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Secret de signature webhook (whsec_…)</Label>
        <Input type="password" value={form.webhook_secret} onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })} placeholder={cfg.has_webhook_secret ? "Configuré — saisir pour remplacer" : "whsec_… (ou « Créer automatiquement » ci-contre)"} data-testid="stripe-webhook-secret" className="h-10 mt-1 font-mono" />
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button onClick={save} disabled={saving} data-testid="stripe-save" className="h-10 bg-[#635BFF] hover:bg-[#4f46e5] text-white"><Save className="h-4 w-4 mr-1" />{saving ? "Vérification…" : "Vérifier & enregistrer"}</Button>
        {cfg.key_source === "db" && <Button variant="outline" onClick={removeKey} data-testid="stripe-remove-key" className="h-10 border-[#E60000] text-[#E60000]"><Trash2 className="h-4 w-4 mr-1" />Retirer la clé</Button>}
      </div>
    </div>
  );
}
